/**
 * Whether this tab is mock-signed-in — the mocked build's session cookie.
 *
 * **Its own module, and the reason is the import graph rather than the concept.**
 * Two things need this answer and only one of them can see `env`: `mockAuth.ts`,
 * which is the gate's mocked pair, and `test/mocks/handlers.ts`, whose `GET /me`
 * must answer 401 without a session the way the real route does (ADR-0050). The
 * Playwright specs import `handlers.ts` for its fixture ids **in Node**, where
 * `import.meta.env` does not exist and `@/env` throws by design — so anything
 * `handlers.ts` reaches must be env-free. Hence the value is *stored* here and
 * *seeded* from `VITE_MOCK_SIGNED_IN` by `main.tsx`, which is the one place that
 * already knows this is a mocked build.
 *
 * **`sessionStorage`, because module state does not survive what the mock does.**
 * `mockAuth`'s `beginSignIn` reloads the page, and a reload re-evaluates every
 * module — so a `let signedIn` would be reset by the very navigation that was
 * supposed to complete the sign-in, and the button would be a dead end. Same
 * storage as `greeting.ts` and the real MSAL cache, so all three end when the
 * tab does.
 *
 * **Unset reads as signed in.** That is the seed's default and also the safe
 * fallback for a browser that refuses storage: the dev loop and the e2e suite
 * land on the app, and `VITE_MOCK_SIGNED_IN=false` — the only way to reach the
 * anonymous landing page without a tenant — is what writes anything here at all.
 */

const KEY = 'bp.mock.signed-in';

export function isMockSignedIn(): boolean {
  try {
    return window.sessionStorage.getItem(KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setMockSignedIn(signedIn: boolean): void {
  try {
    window.sessionStorage.setItem(KEY, String(signedIn));
  } catch {
    /* see above — a mocked build that cannot remember is still a mocked build */
  }
}

/**
 * Whether this tab has ever decided, as opposed to inheriting the seed.
 *
 * `main.tsx` uses it to apply `VITE_MOCK_SIGNED_IN` **once per tab** rather than
 * once per load: the env var is the *initial* value, and a reload after
 * `beginSignIn` must not put it back.
 */
export function mockSignInDecided(): boolean {
  try {
    return window.sessionStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}
